import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import GoogleAd from '../components/GoogleAd';
import LanguageSwitcher from '../components/LanguageSwitcher';
import './Home.css';

function Home() {
  const [title, setTitle] = useState('');
  const [tasksText, setTasksText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Procesar el texto de tareas (una por línea)
    const tasks = tasksText
      .split('\n')
      .map(task => task.trim())
      .filter(task => task.length > 0);

    if (tasks.length === 0) {
      setError(t('errorMinTasks'));
      return;
    }

    setLoading(true);

    try {
      const response = await fetch('/api/lists', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: title.trim() || (language === 'es' ? 'Mi Lista de Tareas' : 'My Task List'),
          tasks: tasks
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Redirigir a la lista creada
        navigate(`/list/${data.listId}`);
      } else {
        setError(data.error || t('errorLoading'));
      }
    } catch (err) {
      console.error('Error:', err);
      setError(t('errorConnection'));
    } finally {
      setLoading(false);
    }
  };

  const handleExample = () => {
    if (language === 'es') {
      setTitle('Lista de Compras');
      setTasksText('Leche\nPan\nHuevos\nFrutas\nVerduras\nCafé\nYogurt');
    } else {
      setTitle('Shopping List');
      setTasksText('Milk\nBread\nEggs\nFruits\nVegetables\nCoffee\nYogurt');
    }
  };

  return (
    <div className="home">
      <div className="home-header">
        <div className="container fade-in">
          <div className="hero">
            <div className="hero-top">
              <h1 className="hero-title">
                <img 
                  src="/logo.png" 
                  alt="Taskit" 
                  className="logo-image"
                />
              </h1>
              <LanguageSwitcher />
            </div>
            <p className="hero-subtitle">
              {t('heroSubtitle')}
            </p>
            <p className="hero-description">
              {t('heroDescription')}
            </p>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="home-content">
          <div className="main-section">
            <div className="card slide-in">
              <form onSubmit={handleSubmit} className="task-form">
                <div className="form-group">
                  <label htmlFor="title" className="form-label">
                    {t('titleLabel')}
                  </label>
                  <input
                    type="text"
                    id="title"
                    className="input"
                    placeholder={t('titlePlaceholder')}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    maxLength={100}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="tasks" className="form-label">
                    {t('tasksLabel')}
                  </label>
                  <textarea
                    id="tasks"
                    className="input textarea"
                    placeholder={t('tasksPlaceholder')}
                    value={tasksText}
                    onChange={(e) => setTasksText(e.target.value)}
                    required
                  />
                  <div className="char-count">
                    {tasksText.split('\n').filter(t => t.trim()).length} {t('tasksCount')}
                  </div>
                </div>

                {error && (
                  <div className="error-message fade-in">
                    {error}
                  </div>
                )}

                <div className="form-actions">
                  <button
                    type="button"
                    className="button button-secondary"
                    onClick={handleExample}
                    disabled={loading}
                  >
                    {t('exampleButton')}
                  </button>
                  <button
                    type="submit"
                    className="button button-primary"
                    disabled={loading}
                  >
                    {loading ? t('generatingButton') : t('generateButton')}
                  </button>
                </div>
              </form>
            </div>

            <div className="features">
              <div className="feature-card">
                <div className="feature-icon">→</div>
                <h3>{t('featureFast')}</h3>
                <p>{t('featureFastDesc')}</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon">∞</div>
                <h3>{t('featureUnique')}</h3>
                <p>{t('featureUniqueDesc')}</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon">◉</div>
                <h3>{t('featureMulti')}</h3>
                <p>{t('featureMultiDesc')}</p>
              </div>
            </div>
          </div>

          {/* Google Ads */}
          <div className="ad-section">
            <GoogleAd slot="top-banner" format="horizontal" />
          </div>
        </div>
      </div>

      <footer className="footer">
        <div className="container">
          <p className="footer-content">
            {t('footerText')}{' '}
            <a 
              href="https://www.instagram.com/mikejauregui/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="footer-link"
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="currentColor"
                className="instagram-icon"
              >
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
              {t('footerBy')}
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}

export default Home;

